/** @jsxImportSource @emotion/react */

import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Modal } from "antd";
import { UserApi } from "api/user";
import { ReactComponent as InfoIcon } from "assets/mobile/icons/info.svg";
import InputPassword from "components/common/InputPassword";
import MESSAGE from "constants/message";
import { PATH } from "constants/path";
import { REGEX_PASSWORD } from "constants/regex";
import { ResponseCode } from "constants/response";
import { useAppLogout } from "hooks/useLogout";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectUsername } from "store/auth";
import * as yup from "yup";

import { MChangePasswordStyle } from "./index.style";

interface MChangePasswordProps {
  onCancel?: () => void;
}

interface FormData {
  oldpass: string;
  password: string;
  passconf: string;
}
const schema = yup
  .object({
    oldpass: yup.string().trim().required("Vui lòng nhập mật khẩu cũ"),
    password: yup
      .string()
      .trim()
      .matches(REGEX_PASSWORD, "Mật khẩu không hợp lệ")
      .required("Vui lòng nhập mật khẩu mới"),
    passconf: yup
      .string()
      .trim()
      .oneOf(
        [yup.ref("password")],
        "Nội dung bạn nhập không giống với mật khẩu mới",
      )
      .required("Vui lòng nhập lại mật khẩu mới"),
  })
  .required();

const MChangePassword = ({ onCancel }: MChangePasswordProps) => {
  const _username =
    useSelector(selectUsername) || localStorage.getItem("username") || "";
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      oldpass: "",
      password: "",
      passconf: "",
    },
  });
  const onLogout = useAppLogout();
  const navigate = useNavigate();

  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [modal, contextHolder] = Modal.useModal();
  const confirm = () => {
    modal.confirm({
      title: MESSAGE.notification,
      icon: null,
      content: MESSAGE.changePassword.success,
      okText: MESSAGE.confirm,
      cancelButtonProps: { style: { display: "none" } },
      centered: true,
      className: "non-reset",
      onOk: handleConfirm,
    });
  };

  const handleConfirm = () => {
    onLogout();
    navigate(PATH.Account.login);
  };

  const onSubmit = async (form: FormData) => {
    try {
      setErrorMsg("");
      setIsLoadingForm(true);
      const { status, msg } = await UserApi.changePassword(_username, {
        ...form,
        username: _username,
      });
      if (status === ResponseCode.SUCCESS) {
        reset();
        confirm();
      } else {
        setErrorMsg(msg);
      }
    } finally {
      setIsLoadingForm(false);
    }
  };

  return (
    <div css={MChangePasswordStyle.self}>
      <div className="non-reset">
        <Alert
          message={
            <>
              <InfoIcon /> {MESSAGE.changePassword.warning}
            </>
          }
          type="info"
          css={MChangePasswordStyle.alertInfo}
        />
      </div>
      <form
        css={MChangePasswordStyle.formWrapper}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div css={MChangePasswordStyle.formGroup}>
          <label htmlFor="oldpass" css={MChangePasswordStyle.formLabel}>
            Mật khẩu ban đầu
          </label>
          <InputPassword
            value={watch("oldpass")}
            id="oldpass"
            control={control}
            name="oldpass"
            placeholder="Nhập mật khẩu cũ"
            validate_status={errors.oldpass ? "error" : "success"}
            error={errors.oldpass?.message}
            disabled={isLoadingForm}
            margin_bottom="0"
          />
        </div>
        <div css={MChangePasswordStyle.formGroup}>
          <label htmlFor="newpass" css={MChangePasswordStyle.formLabel}>
            Mật khẩu mới
          </label>
          <InputPassword
            value={watch("password")}
            id="newpass"
            control={control}
            name="password"
            placeholder="Hơn 6 ký tự bao gồm chữ cái và số"
            validate_status={errors.password ? "error" : "success"}
            error={errors.password?.message}
            disabled={isLoadingForm}
            margin_bottom="0"
          />
        </div>
        <div css={MChangePasswordStyle.formGroup}>
          <label htmlFor="passconf" css={MChangePasswordStyle.formLabel}>
            Xác nhận mật khẩu mới
          </label>
          <InputPassword
            value={watch("passconf")}
            id="passconf"
            control={control}
            name="passconf"
            placeholder="Xin nhập lại mật khẩu mới của bạn"
            validate_status={errors.passconf ? "error" : "success"}
            error={errors.passconf?.message}
            disabled={isLoadingForm}
            margin_bottom="0"
          />
        </div>

        {!!errorMsg && (
          <div className="non-reset">
            <Alert message={errorMsg} type="error" />
          </div>
        )}

        <div css={MChangePasswordStyle.formBtnWrapper}>
          <Button
            htmlType="submit"
            css={MChangePasswordStyle.formBtn}
            loading={isLoadingForm}
          >
            Thay đổi
          </Button>
          <Button
            css={MChangePasswordStyle.formBtn}
            onClick={() => onCancel && onCancel()}
          >
            Huỷ bỏ
          </Button>
        </div>
      </form>
      {contextHolder}
    </div>
  );
};

export default MChangePassword;
